import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Autocomplete, CircularProgress, TextField } from "@mui/material"
import { collection, getDocs, limit, query, where } from "firebase/firestore"
import type { WidgetProps } from "@rjsf/utils"
import htmr from "htmr"
import { isGoalRaffle } from "react-ui-components/components/raffle/raffleGoalUtils"
import type { IRaffle } from "react-ui-components/components/models/IRaffle"

import { db } from "../firebase"

// Titles can contain rich-text HTML (see IRaffle.title), so the plain-text label used for the
// text field's input value and MUI's built-in filtering strips tags, while renderOption below
// renders the HTML for display.
const stripHtml = (value: string) => value.replace(/<[^>]*>/g, "").trim()

type EligibleRaffle = Pick<IRaffle, "raffleMode" | "goalSettings" | "type"> & {
  id: string
  title: string
  raffleDate?: number
}

// A raffle is "eligible" to receive tickets from a product's Win Box the same way it's
// eligible to show its own Win button to end users: not disabled, and not already past its
// drawing date (goal-mode raffles have no drawing date, so they stay eligible indefinitely).
// Mirrors win-facil-web-app's `isVisibleRaffle` (src/contexts/raffleVisibility.ts) without
// importing across apps.
const isEligibleRaffle = (raffle: EligibleRaffle, now = Date.now()) => {
  if (isGoalRaffle(raffle)) {
    return true
  }

  return typeof raffle.raffleDate === "number" && raffle.raffleDate >= now
}

// -------------------- RaffleSelectWidget --------------------
// Lets an admin pick which raffle a product's Win Box grants tickets to, since a service isn't
// itself a raffle. Modeled on FeaturedBusinesses.tsx's SponsorSelectWidget/ServiceSelectWidget
// (Firestore-backed Autocomplete with search-as-you-type).
export const RaffleSelectWidget: React.FC<WidgetProps> = (props) => {
  const { value, onChange, disabled } = props

  const [items, setItems] = useState<EligibleRaffle[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState("")

  const load = useCallback(async (term: string) => {
    setLoading(true)
    setError(null)

    try {
      const baseRef = collection(db, "raffles")
      const q = term
        ? query(
            baseRef,
            where("disabled", "==", false),
            where("type", "==", "Raffle"),
            where("title", ">=", term),
            where("title", "<=", term + "\uf8ff"),
            limit(50),
          )
        : query(baseRef, where("disabled", "==", false), where("type", "==", "Raffle"), limit(50))

      const snap = await getDocs(q)
      const now = Date.now()
      const rows: EligibleRaffle[] = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<EligibleRaffle, "id">) }))
        .filter((raffle) => isEligibleRaffle(raffle, now))
        .sort((a, b) => (a.title || "").localeCompare(b.title || ""))
      setItems(rows)
    } catch (err) {
      console.error("Error loading raffles:", err)
      setError("Failed to load raffles")
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load("")
  }, [load])

  const selected = useMemo(() => items.find((x) => x.id === value) ?? null, [items, value])

  return (
    <Autocomplete<EligibleRaffle>
      options={items}
      value={selected}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onChange={(_, v) => onChange(v?.id ?? "")}
      inputValue={search}
      onInputChange={(_, v, reason) => {
        setSearch(v)
        // MUI re-syncs the displayed text to the selected option's label right after a
        // selection (reason "reset") and clears it on blur-revert (reason "clear") — refetching
        // on those synthetic events, not just real typing, produced a fresh `items` array each
        // time, which fed back into a new `selected` reference and looped.
        if (reason !== "input") {
          return
        }
        load(v).catch(() => {
          setLoading(false)
          setError("Failed to search raffles")
        })
      }}
      getOptionLabel={(o) => stripHtml(o.title || o.id)}
      renderOption={(optionProps, o) => (
        <li {...optionProps} key={o.id}>
          {o.title ? htmr(o.title) : o.id}
        </li>
      )}
      loading={loading}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Raffle"
          variant="outlined"
          fullWidth
          error={!!error}
          helperText={error || "Only active, undrawn raffles are shown"}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default RaffleSelectWidget
